// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { Close, Error } from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/system";
import stylesDef from "./styles";

interface NearmapConfidenceDialogProps {
  showDialog: boolean;
  onProceed: () => void;
  onClose: () => void;
  layersWithConfidence: IDeliverable[];
  nearmapConfidenceAcknowledged: boolean;
  setNearmapConfidenceAcknowledged: (value: boolean) => void;
}

const NearmapConfidenceDialog = ({
  showDialog,
  onProceed,
  onClose,
  layersWithConfidence,
  nearmapConfidenceAcknowledged,
  setNearmapConfidenceAcknowledged,
}: NearmapConfidenceDialogProps) => {
  const theme = useTheme();
  const styles = stylesDef(theme);

  const lowConfidenceLayers = layersWithConfidence.filter(
    (layer) => layer.nearmapConfidence === "low"
  );
  const noConfidenceLayers = layersWithConfidence.filter(
    (layer) => layer.nearmapConfidence === "none"
  );

  return (
    <Dialog open={showDialog} onClose={onClose} sx={styles.dialog}>
      <Box sx={styles.dialogTitle}>
        <Error fontSize="large" sx={styles.warningIcon} />
        <Typography variant="h3" sx={styles.titleText}>
          Nearmap Imagery Limitations Notice
        </Typography>
      </Box>
      <IconButton aria-label="close" onClick={onClose} sx={styles.closeIcon}>
        <Close />
      </IconButton>
      <DialogContent sx={styles.dialogContent}>
        <Box sx={styles.nearmapDialogBox}>
          <Typography variant="body1">
            The following layers will have limited or no delivery:
          </Typography>
          <List>
            {lowConfidenceLayers.length > 0 && (
              <ListItem sx={styles.listItem}>
                <ListItemText
                  sx={styles.listItemText}
                  primary={
                    <Typography variant="body1">
                      <b>• Reduced accuracy:</b>{" "}
                      {lowConfidenceLayers.map((layer) => layer.name).join(", ")}
                    </Typography>
                  }
                />
              </ListItem>
            )}
            {noConfidenceLayers.length > 0 && (
              <ListItem sx={styles.listItem}>
                <ListItemText
                  sx={styles.listItemText}
                  primary={
                    <Typography variant="body1">
                      <b>• Not available:</b>{" "}
                      {noConfidenceLayers.map((layer) => layer.name).join(", ")}
                    </Typography>
                  }
                />
              </ListItem>
            )}
          </List>
          <FormControlLabel
            label={
              <Typography variant="body1">
                I understand and wish to proceed.
              </Typography>
            }
            control={
              <Checkbox
                checked={nearmapConfidenceAcknowledged}
                color="primary"
                sx={styles.checked}
                onChange={(event) =>
                  setNearmapConfidenceAcknowledged(event.target.checked)
                }
              />
            }
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          disabled={!nearmapConfidenceAcknowledged}
          onClick={onProceed}
          color="primary"
        >
          Proceed
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NearmapConfidenceDialog;
